"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.removeAllocationQuerySchema = exports.patchAllocationRequestSchema = exports.saveAllocationsRequestSchema = exports.saveAllocationRequestSchema = exports.allocationSchema = exports.ALLOCATION_BOOKINGS = exports.allocationDaySchema = exports.ALLOCATION_DISTRIBUTIONS = void 0;
exports.toQuarter = toQuarter;
exports.distributeHours = distributeHours;
exports.allocationHours = allocationHours;
exports.allocationBilledHours = allocationBilledHours;
const zod_1 = require("zod");
const identifier_1 = require("../identifier");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Allocations — C52, Phase 4. Who is planned onto what, and on which days.  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **AN ALLOCATION IS A MAP OF DAYS, AND EVERY OTHER SHAPE IS A VIEW OF IT.** The mockup's own
 * record reads `{ from, to, hpd, days? }` — hours per day, with an optional override — but its
 * writer never produces the first form: `allocBuild` always returns a `days` map and `allocSave`
 * derives `from`/`to` from its first and last key. So `hpd` is a fixture shorthand that `allocMap`
 * normalises away on the first read, and this module keeps the one representation.
 *
 * ⚠️ **The hours here are PLANNED, and planned is not logged.** C44's kernel answers what a day
 * expected and what was worked; this answers what somebody intends. The two are compared on the
 * Erőforrás grid and in the finance dashboard, and conflating them would make a plan look like a
 * fact the moment it was typed.
 */
const wireDateSchema = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** The mockup's own two: `ALLOC_DIST = { even, earliest }`. */
exports.ALLOCATION_DISTRIBUTIONS = ['even', 'earliest'];
exports.allocationDaySchema = zod_1.z.object({
    date: wireDateSchema,
    /** ⚠️ A string, like every other `numeric` on the wire. Parse before arithmetic. */
    hours: zod_1.z.string(),
});
/**
 * One person planned onto one line item.
 *
 * ⚠️ **`days` is the whole truth and `fromDate`/`toDate` are its first and last.** They travel
 * because the screen sorts and groups by them, not because they are separately editable.
 */
/**
 * ⚠️⚠️ **HARD IS A COMMITMENT, SOFT IS A PENCIL MARK.** The mockup's own words: hard is
 * *“Elkötelezett kapacitás — beleszámít a terhelésbe”*, soft is *“Előjegyzés — jelzésértékű, a
 * másolás is örökli”*. A booking for work that may not land should not make somebody look full —
 * and a planner who cannot pencil one in keeps it somewhere this product cannot see.
 *
 * ⚠️ **A copy inherits it**, which is the second half of that sentence and the reason it is a
 * property of the allocation rather than of the cell somebody happened to click.
 */
exports.ALLOCATION_BOOKINGS = ['hard', 'soft'];
exports.allocationSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** `allocation_4f8a1c2e` — the public identifier `0194` mints beside the uuid; see `identifier.ts`. */
    identifier: (0, identifier_1.identifierSchema)('allocation'),
    memberId: zod_1.z.string(),
    memberName: zod_1.z.string(),
    memberAvatarUrl: zod_1.z.string().nullable(),
    /**
     * ⚠️ **C68 · what this person's generated face is drawn from**, when there is no picture
     * above. The normalised email, produced by `wireAvatarSeed` — nullable exactly like the URL
     * beside it, because a row with nobody in it has no face either.
     */
    memberAvatarSeed: zod_1.z.string().nullable(),
    lineItemId: zod_1.z.string(),
    lineItemName: zod_1.z.string(),
    /** The project the line item's contract belongs to — the grid groups by it. */
    projectId: zod_1.z.string().nullable(),
    projectName: zod_1.z.string().nullable(),
    fromDate: wireDateSchema,
    toDate: wireDateSchema,
    distribution: zod_1.z.enum(exports.ALLOCATION_DISTRIBUTIONS),
    /**
     * ⚠️ **Whether this booking is a commitment.** See `ALLOCATION_BOOKINGS`. Every allocation made
     * before the distinction existed is `hard`, because that is what it meant when it was made.
     */
    booking: zod_1.z.enum(exports.ALLOCATION_BOOKINGS),
    /**
     * ⚠️ **Planned overtime — a forecast, not a claim.** `0111`'s claims stay the record of what was
     * worked; this says a release week is expected to cost time-and-a-half, and `multiplier` is what
     * turns planned hours into planned cost.
     */
    overtimeTypeId: zod_1.z.string().nullable(),
    overtimeTypeName: zod_1.z.string().nullable(),
    /** The multiplier in force for that type, or `'1.00'` when there is no overtime on the row. */
    multiplier: zod_1.z.string(),
    note: zod_1.z.string(),
    days: zod_1.z.array(exports.allocationDaySchema),
    version: zod_1.z.number().int(),
});
/**
 * ⚠️⚠️ **A WRITE NAMES A SPAN AND A TOTAL, NEVER A DAY MAP.** The planner's cell editor types
 * *"how many hours in this column"* and the kernel decides which days carry them — `resCellCommit`
 * does exactly this, and a client that sent its own map would be re-implementing `allocDistribute`
 * against capacity figures it would have to fetch first.
 *
 * ⚠️ **`hours: 0` is how a span is CLEARED**, and an allocation left with no days at all is
 * deleted rather than kept spanning nothing. `allocSave` does the same: *"if (!keys.length) next =
 * list.filter(a => a.id !== rec.id)"*.
 */
exports.saveAllocationRequestSchema = zod_1.z.object({
    /**
     * ⚠️⚠️ **WHICH ALLOCATION, WHEN THERE IS MORE THAN ONE.** `0143` dropped the one-per-pair
     * index, because the cell pop-over holds several bookings for one person on one deliverable and
     * lets each carry its own overtime, priority and booking kind. So a typed cell has to say where
     * it lands.
     *
     * ⚠️ **Absent is still valid and still means the pair**, which keeps every existing caller
     * working: the server uses it when the pair resolves to exactly one row, and refuses rather than
     * guessing when it does not. `0129`'s hazard — *“which one a typed cell landed in would depend
     * on insertion order”* — is answered by refusing, not by an index.
     *
     * ⚠️⚠️ **AND `null` MEANS *MAKE A NEW ONE*, WHICH IS NOT THE SAME AS ABSENT.** The pop-over's
     * *Új allokáció hozzáadása* on a pair that already has a booking would otherwise be
     * indistinguishable from a typo in the existing one — and, since a bare pair over several rows
     * is refused, it could not be written at all. Three states, because there are three intentions.
     */
    allocationId: zod_1.z.string().nullable().optional(),
    memberId: zod_1.z.string(),
    lineItemId: zod_1.z.string(),
    fromDate: wireDateSchema,
    toDate: wireDateSchema,
    hours: zod_1.z.number().min(0).max(10000),
    distribution: zod_1.z.enum(exports.ALLOCATION_DISTRIBUTIONS).optional(),
    booking: zod_1.z.enum(exports.ALLOCATION_BOOKINGS).optional(),
    overtimeTypeId: zod_1.z.string().nullable().optional(),
    note: zod_1.z.string().max(500).optional(),
    version: zod_1.z.number().int().optional(),
});
/**
 * ⚠️⚠️ **A RANGE IS ONE REQUEST.** The planner's copy and clear tools apply a value across every
 * column between two cells — sixty-odd spans in a quarter of day columns. Sent one at a time they
 * are sixty round trips and sixty redraws, and a copy that fails halfway leaves a plan nobody typed.
 *
 * ⚠️ **Capped at 200**, which is wider than any window the grid draws and narrow enough that one
 * request cannot become a job.
 */
exports.saveAllocationsRequestSchema = zod_1.z.object({
    writes: zod_1.z.array(exports.saveAllocationRequestSchema).min(1).max(200),
});
/**
 * ⚠️⚠️ **THE ALLOCATION'S OWN FIELDS, WHICH ARE NOT SPAN FIELDS — AND THAT IS THE WHOLE REASON
 * THIS EXISTS SEPARATELY.** `overtimeTypeId`, `distribution` and `note` are columns on the
 * allocation: one release-week rate for the row, not a different one per column. The mockup keeps
 * them in a per-CELL pop-over, which reads as *set overtime for this week* and sets it for the
 * whole plan — the kind of control that is wrong only in the week somebody needed it to be right.
 *
 * ⚠️ **A save cannot carry them.** `saveAllocationRequestSchema` names a span and a total, and an
 * absent field there means *leave it alone* precisely so the planner's cell editor cannot clear
 * somebody's overtime by widening a column. So changing one is its own request.
 */
exports.patchAllocationRequestSchema = zod_1.z.object({
    overtimeTypeId: zod_1.z.string().nullable().optional(),
    distribution: zod_1.z.enum(exports.ALLOCATION_DISTRIBUTIONS).optional(),
    booking: zod_1.z.enum(exports.ALLOCATION_BOOKINGS).optional(),
    note: zod_1.z.string().max(500).optional(),
    version: zod_1.z.number().int().optional(),
    /**
     * ⚠️⚠️ 27 Oct 2026 · **THE COLUMN A DISTRIBUTION CHANGE APPLIES TO.** Picking *Even* in one
     * week's pop-over re-spread the allocation's WHOLE span — a March–June plan smeared across four
     * months, an *earliest* one piling its overflow on the last day of June. Given both, only the
     * hours inside this span are re-spread, inside it; the mode is still stored on the row. Absent
     * (the API, the Agent), the whole span is re-spread as before.
     */
    fromDate: wireDateSchema.optional(),
    toDate: wireDateSchema.optional(),
});
/**
 * ⚠️ 3 Nov 2026 · F4.1 · `DELETE /allocations/:id?from=` — **the member's own today**. Days on
 * or after it go and the past stays; absent, the whole allocation goes (the API, the importer).
 */
exports.removeAllocationQuerySchema = zod_1.z.object({
    from: wireDateSchema.optional(),
});
/**
 * ⚠️ **Quarter hours.** `allocDistribute` rounds every share with `Math.round(v * 4) / 4`, so eight
 * hours over three days is 2.75 / 2.75 / 2.5 rather than three recurring decimals — and the last
 * day absorbs the remainder so the total is exactly what was asked for.
 */
function toQuarter(value) {
    return Math.round(value * 4) / 4;
}
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE KERNEL: HOW A TOTAL LANDS ON DAYS. PURE, AND TESTED AS SUCH.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `days` are the person's ALLOCATABLE days in the span — their schedule, their leave and the
 * workspace's calendar already applied, which is C44's job and not this function's. A span with
 * none of them takes no hours at all, which is the honest answer: there is nowhere to put them.
 *
 * **`even`** spreads the total across the days in quarters, the first days carrying one quarter
 * more each until the remainder is used up — and, given `freeOn`, never past what a day has free
 * while another day still has room (see the levelling note in the body).
 *
 * **`earliest`** fills each day up to whatever capacity it has left and moves on — *"erőforrás
 * legkorábbi allokálása"*. `freeOn` answers how many hours that day still has, EXCLUDING this
 * allocation's own current hours, or the second edit of one span would refuse to fit what the first
 * one had already put there.
 *
 * ⚠️ **Overflow lands on the LAST day rather than being dropped**, which is the mockup's own
 * choice and the only one that keeps the total honest: an over-allocated day is a visible problem
 * the grid flags, and hours that silently vanished are not.
 */
function distributeHours(input) {
    const out = {};
    const { days, hours } = input;
    if (days.length === 0 || hours <= 0)
        return out;
    const last = days[days.length - 1];
    if (input.mode === 'earliest') {
        const freeOn = input.freeOn ?? (() => Number.POSITIVE_INFINITY);
        let remaining = hours;
        for (const day of days) {
            if (remaining <= 0.01)
                break;
            const take = toQuarter(Math.min(Math.max(0, freeOn(day)), remaining));
            if (take > 0) {
                out[day] = take;
                remaining -= take;
            }
        }
        if (remaining > 0.01)
            out[last] = toQuarter((out[last] ?? 0) + remaining);
        return out;
    }
    /* ⚠️⚠️ **IN WHOLE QUARTERS, AND THE REMAINDER IS SPREAD RATHER THAN PILED ON ONE DAY** (15 Oct
     * 2026). The first version rounded the share once and let the last day take `hours - placed`:
     * a share rounded UP over-placed by up to 0.125 h a day, the last day's negative remainder was
     * skipped and the stored total came out high (1222 h stored as 1239.75); a share rounded DOWN
     * piled the whole shortfall on the last day, past 24 h, and the database refused the allocation
     * outright. Every day gets the floored share and the leftover quarters go one each to the first
     * days, so the total is exact.
     *
     * ⚠️⚠️ **EVEN WITHIN WHAT EACH DAY HAS FREE — 27 Oct 2026, the product owner.** *"Aim for even
     * first; if it goes over, lower it for that day and split the extra evenly on the other days."*
     * So the share is levelled like water: a day with less free than the share gets what it has, and
     * the rest is shared again among the days that still have room, until it is all placed. Only
     * when every day is full does anything go past capacity — and then the excess is spread evenly
     * across all the days, so an overbooking shows as a level the grid flags rather than one tower. */
    const quarters = Math.round(hours * 4);
    const freeOn = input.freeOn ?? (() => Number.POSITIVE_INFINITY);
    const room = days.map((day) => Math.max(0, Math.floor(freeOn(day) * 4 + 1e-9)));
    const placed = days.map(() => 0);
    let remaining = quarters;
    let open = days.map((_, index) => index).filter((index) => (room[index] ?? 0) > 0);
    while (remaining > 0 && open.length > 0) {
        const share = Math.floor(remaining / open.length);
        if (share === 0) {
            /* Fewer quarters than open days: one each to the first of them. */
            for (const index of open) {
                if (remaining === 0)
                    break;
                placed[index] = (placed[index] ?? 0) + 1;
                remaining -= 1;
            }
            break;
        }
        const still = [];
        for (const index of open) {
            const take = Math.min(share, (room[index] ?? 0) - (placed[index] ?? 0));
            placed[index] = (placed[index] ?? 0) + take;
            remaining -= take;
            if ((room[index] ?? 0) - (placed[index] ?? 0) > 0)
                still.push(index);
        }
        open = still;
    }
    /* Nowhere left with room: the excess evenly over every day, the first days a quarter more. */
    if (remaining > 0) {
        const base = Math.floor(remaining / days.length);
        const extra = remaining - base * days.length;
        days.forEach((_, index) => {
            placed[index] = (placed[index] ?? 0) + base + (index < extra ? 1 : 0);
        });
    }
    days.forEach((day, index) => {
        const value = (placed[index] ?? 0) / 4;
        if (value > 0)
            out[day] = value;
    });
    return out;
}
/**
 * What an allocation plans within a window — the whole thing when no window is given.
 *
 * ⚠️ **Inclusive at both ends**, like every other range in this product.
 */
function allocationHours(days, from, to) {
    return days.reduce((sum, day) => {
        if (from !== undefined && day.date < from)
            return sum;
        if (to !== undefined && day.date > to)
            return sum;
        return sum + Number(day.hours);
    }, 0);
}
/**
 * ⚠️ **Planned hours BILLED — the gross multiplied by the overtime rate.** `allocMul` is the
 * mockup's own, and the distinction matters everywhere money is: a release week planned at 1.5×
 * costs half as much again as the same hours in an ordinary one.
 */
function allocationBilledHours(allocation, from, to) {
    return allocationHours(allocation.days, from, to) * Number(allocation.multiplier);
}
//# sourceMappingURL=allocation.js.map